"use client"

import styled from "@emotion/styled"
import { sharedColors } from "@eurodrive/ui/theme/colors"
import {
  Box,
  IconButton,
  InputAdornment,
  TextField,
  Typography,
} from "@mui/material"
import debounce from "debounce"
import Image from "next/image"
import { useCallback, useEffect, useState } from "react"
import { SearchFilters, SearchFiltersActions } from "./Filters/useSearchFilters"

const StyledSearchContainer = styled(Box)`
  display: flex;
  flex-direction: column;
  width: 100%;
  gap: 16px;
`

const StyledTextField = styled(TextField)`
  .MuiOutlinedInput-root {
    border-radius: 8px;
    background-color: ${sharedColors.white};
    border: 1px solid rgba(0, 0, 0, 0.2);
    padding: 8px 12px;
    display: flex;
    align-items: center;
    gap: 4px;

    &:hover {
      border-color: rgba(0, 0, 0, 0.3);
    }

    &.Mui-focused {
      border-color: ${sharedColors.darkGreen};
      box-shadow: 0 0 0 1px ${sharedColors.darkGreen};
    }

    .MuiOutlinedInput-notchedOutline {
      border: none;
    }

    .MuiInputBase-input {
      font-family: "Assistant", sans-serif;
      font-size: 16px;
      font-weight: 400;
      line-height: 1.4;
      color: ${sharedColors.black};
      padding: 0;
      flex: 1;

      &::placeholder {
        color: rgba(0, 0, 0, 0.2);
        opacity: 1;
      }
    }
  }
`

const StyledIconButton = styled(IconButton)`
  padding: 4px;
  margin: 0;
  min-width: 12px;
  width: 12px;
  height: 12px;
  border-radius: 4px;

  &:hover {
    background-color: rgba(0, 0, 0, 0.04);
  }

  &:active {
    background-color: rgba(0, 0, 0, 0.08);
  }
`

const SearchIconContainer = styled(Box)`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 16px;
  height: 16px;
  margin-inline-end: 4px;
`

const CloseIconContainer = styled(Box)`
  display: flex;
  align-items: center;
  justify-content: center;
  margin-inline-start: 4px;
`

const StyledFilterDetails = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
`

const MobileFilterDetails = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 9px;
  ${(props) => props.theme.breakpoints.up("md")} {
    display: none;
  }
`

const Divider = styled(Box)`
  width: 1px;
  height: 12px;
  background-color: #6f6f6f;
  margin-inline-end: 8px;
  margin-inline-start: 8px;
`

const FilterTagsContainer = styled(Box)`
  display: flex;
  flex-wrap: wrap;
  gap: 10px;
  align-items: center;
`

const FilterTag = styled(Box)`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 4px 8px;
  background-color: #313237;
  border-radius: 4px;
  color: ${sharedColors.white};
  font-family: "Assistant", sans-serif;
  font-size: 16px;
  font-weight: 400;
  line-height: 1.4;
`

const DiscountChip = styled(Box, {
  shouldForwardProp: (prop) => prop !== "active",
})<{ active: boolean }>`
  display: flex;
  align-items: center;
  gap: ${(props) => (props.active ? "8px" : "4px")};
  padding: 4px 8px;
  background-color: ${(props) => (props.active ? "#313237" : "#F3F3F3")};
  border-radius: 4px;
  color: ${(props) => (props.active ? sharedColors.white : sharedColors.black)};
  font-family: "Assistant", sans-serif;
  font-size: 16px;
  font-weight: 400;
  line-height: 1.4;
  cursor: pointer;
  transition: background-color 0.2s ease;

  &:hover {
    background-color: ${(props) => (props.active ? "#3d3d42" : "#e8e8e8")};
  }
`

const TagCloseButton = styled(IconButton)`
  padding: 2px 0px 0px;
  margin: 0;
  width: 8.5px;
  height: auto;

  &:hover {
    background-color: transparent;
  }
`
interface ContentFiltersProps {
  filters: SearchFilters
  actions: SearchFiltersActions
  resultsCount: number
  carTypes: { id: string; label: string; icon: string }[]
  carFeatures: { id: string; label: string }[]
  dealTypeOptions: { value: string; label: string }[]
  manufacturerOptions: { value: string; label: string }[]
  modelOptions: { value: string; label: string }[]
  onMobileFilterClick?: () => void
  minPrice: number
  maxPrice: number
}

const ContentFilters = ({
  filters,
  actions,
  resultsCount,
  carTypes,
  carFeatures,
  dealTypeOptions,
  manufacturerOptions,
  modelOptions,
  onMobileFilterClick,
  minPrice,
  maxPrice,
}: ContentFiltersProps) => {
  const [searchText, setSearchText] = useState(filters.searchQuery)

  // Debounced function to update the actual search query
  const debouncedSetSearchQuery = useCallback(
    debounce((value: string) => {
      actions.setSearchQuery(value)
    }, 500),
    [actions],
  )

  // Update local search text when filters.searchQuery changes (e.g., from URL)
  useEffect(() => {
    setSearchText(filters.searchQuery)
  }, [filters.searchQuery])

  // Calculate the actual number of selected filters
  const getSelectedFiltersCount = () => {
    let count = 0

    // Search query
    if (filters.searchQuery) count++

    // Selected features
    count += filters.selectedFeatures.length

    // Selected car types
    count += filters.selectedCarTypes.length

    // Price range (if different from default)
    if (
      filters.priceRange[0] !== minPrice ||
      filters.priceRange[1] !== maxPrice
    )
      count++

    // Year range
    if (filters.yearFrom || filters.yearTo) count++

    // KM range
    if (filters.kmFrom || filters.kmTo) count++

    // Manufacturer
    if (filters.selectedManufacturer) count++

    // Model
    if (filters.selectedModel) count++

    return count
  }

  const selectedFiltersCount = getSelectedFiltersCount()

  const handleClear = () => {
    setSearchText("")
    actions.setSearchQuery("")
  }

  const handleInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const value = event.target.value
    setSearchText(value) // Immediate UI update
    debouncedSetSearchQuery(value) // Debounced API trigger
  }

  // Generate filter tags
  const getFilterTags = () => {
    const tags = []

    // Search query tag
    if (filters.searchQuery) {
      tags.push({
        id: "search",
        label: filters.searchQuery,
        onRemove: () => actions.setSearchQuery(""),
      })
    }

    // Selected features tags
    filters.selectedFeatures.forEach((featureId) => {
      const feature = carFeatures.find((f) => f.id === featureId)
      if (feature) {
        tags.push({
          id: `feature-${featureId}`,
          label: feature.label,
          onRemove: () => actions.toggleFeature(featureId),
        })
      }
    })

    // Selected car types tags
    filters.selectedCarTypes.forEach((carTypeId) => {
      const carType = carTypes.find((c) => c.id === carTypeId)
      if (carType) {
        tags.push({
          id: `carType-${carTypeId}`,
          label: carType.label,
          onRemove: () => actions.toggleCarType(carTypeId),
        })
      }
    })

    // Price range tag (if different from metadata defaults)
    if (
      filters.priceRange[0] !== minPrice ||
      filters.priceRange[1] !== maxPrice
    ) {
      const priceLabel = `₪${filters.priceRange[0].toLocaleString()} - ₪${filters.priceRange[1].toLocaleString()}`
      tags.push({
        id: "priceRange",
        label: priceLabel,
        onRemove: () => actions.setPriceRange([minPrice, maxPrice]),
      })
    }

    // Year range tag
    if (filters.yearFrom || filters.yearTo) {
      const yearLabel = `${filters.yearFrom || "כל השנים"} - ${filters.yearTo || new Date().getFullYear()}`
      tags.push({
        id: "yearRange",
        label: yearLabel,
        onRemove: () => {
          actions.setYearFrom("")
          actions.setYearTo("")
        },
      })
    }

    // KM range tag
    if (filters.kmFrom || filters.kmTo) {
      const kmLabel =
        filters.kmFrom === "0" && !filters.kmTo
          ? "0 ק״מ"
          : `${filters.kmFrom || "0"} - ${filters.kmTo || "∞"} ק״מ`
      tags.push({
        id: "kmRange",
        label: kmLabel,
        onRemove: () => {
          actions.setKmFrom("")
          actions.setKmTo("")
        },
      })
    }

    // Manufacturer tag
    if (filters.selectedManufacturer) {
      const manufacturer = manufacturerOptions.find(
        (m) => m.value === filters.selectedManufacturer,
      )
      tags.push({
        id: "manufacturer",
        label: manufacturer?.label || filters.selectedManufacturer,
        onRemove: () => actions.setSelectedManufacturer(""),
      })
    }

    // Model tag
    if (filters.selectedModel) {
      const model = modelOptions.find((m) => m.value === filters.selectedModel)
      tags.push({
        id: "model",
        label: model?.label || filters.selectedModel,
        onRemove: () => actions.setSelectedModel(""),
      })
    }

    return tags
  }

  const filterTags = getFilterTags()

  return (
    <StyledSearchContainer>
      <StyledTextField
        value={searchText}
        onChange={handleInputChange}
        placeholder={"חפש רכב..."}
        variant="outlined"
        fullWidth
        autoComplete="search"
        inputProps={{
          "aria-label": "חיפוש רכבים",
        }}
        InputProps={{
          endAdornment: (
            <InputAdornment position="start">
              <SearchIconContainer>
                <Image
                  src="/icons/search-icon.svg"
                  alt=""
                  width={16}
                  height={16}
                  style={{
                    filter: `brightness(0) saturate(100%) invert(13%) sepia(28%) saturate(2749%) hue-rotate(93deg) brightness(94%) contrast(97%)`, // Convert to #194F20
                  }}
                />
              </SearchIconContainer>
            </InputAdornment>
          ),
          startAdornment: searchText && (
            <InputAdornment position="end">
              <CloseIconContainer>
                <StyledIconButton
                  onClick={handleClear}
                  aria-label="נקה חיפוש"
                  size="small"
                >
                  <Image
                    src="/icons/close-icon.svg"
                    alt=""
                    width={12}
                    height={12}
                    style={{
                      filter: `brightness(0) saturate(100%) invert(0%) sepia(0%) saturate(0%) hue-rotate(0deg) brightness(0%) contrast(100%)`, // Convert to black
                    }}
                  />
                </StyledIconButton>
              </CloseIconContainer>
              <Divider />
            </InputAdornment>
          ),
        }}
      />

      {/* Results count and filter tags */}
      <Box display="flex" flexDirection="column" gap="16px">
        <StyledFilterDetails>
          <Typography variant="body2" component={"h2"} fontWeight={400}>
            מצאנו <strong>{resultsCount}</strong> רכבים שתואמים לחיפוש שלך
          </Typography>

          <MobileFilterDetails>
            <Typography variant="body2" fontWeight={700}>
              ({selectedFiltersCount})
            </Typography>
            <IconButton onClick={onMobileFilterClick} aria-label="פתח סינונים">
              <Image
                alt=""
                src={"/icons/mobileFilters.svg"}
                width={20}
                height={20}
              />
            </IconButton>
          </MobileFilterDetails>
        </StyledFilterDetails>

        {(filterTags.length > 0 || filters.showDiscountsOnly) && (
          <FilterTagsContainer>
            {filters.showDiscountsOnly && (
              <FilterTag>
                <TagCloseButton
                  onClick={() => actions.setShowDiscountsOnly(false)}
                  aria-label="הסר סינון מבצעים"
                  size="small"
                >
                  <Image
                    src="/icons/tag-close-icon.svg"
                    alt=""
                    width={8}
                    height={8}
                    style={{
                      filter: `brightness(0) saturate(100%) invert(100%) sepia(0%) saturate(0%) hue-rotate(0deg) brightness(100%) contrast(100%)`, // Convert to white
                    }}
                  />
                </TagCloseButton>
                <Box
                  display="flex"
                  alignItems="center"
                  justifyContent="center"
                  width="16px"
                  height="16px"
                >
                  <Image
                    src="/icons/sale-icon.svg"
                    alt=""
                    width={16}
                    height={16}
                    style={{
                      filter: `brightness(0) saturate(100%) invert(100%) sepia(0%) saturate(0%) hue-rotate(0deg) brightness(100%) contrast(100%)`, // white
                    }}
                  />
                </Box>
                מבצעים
              </FilterTag>
            )}
            {filterTags.map((tag) => (
              <FilterTag key={tag.id}>
                <TagCloseButton
                  onClick={tag.onRemove}
                  aria-label={`הסר סינון ${tag.label}`}
                  size="small"
                >
                  <Image
                    src="/icons/tag-close-icon.svg"
                    alt=""
                    width={8}
                    height={8}
                    style={{
                      filter: `brightness(0) saturate(100%) invert(100%) sepia(0%) saturate(0%) hue-rotate(0deg) brightness(100%) contrast(100%)`, // Convert to white
                    }}
                  />
                </TagCloseButton>
                {tag.label}
              </FilterTag>
            ))}
          </FilterTagsContainer>
        )}
      </Box>
    </StyledSearchContainer>
  )
}

export default ContentFilters
